import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { ShoppingCart, Package, Sparkles, X } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import { trackBundleView, trackBundleAddToCart } from '../../utils/tracking';

export const BundleDetailSheet = ({ bundleId, open, onClose }) => {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    if (open && bundleId) {
      fetchBundle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bundleId]);

  const fetchBundle = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundles/${bundleId}`);
      const data = await response.json();
      setBundle(data);
      trackBundleView(data.id, data.name);
    } catch (error) {
      console.error('Failed to fetch bundle:', error);
      toast.error('Failed to load bundle');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBundle = () => {
    if (bundle?.products && bundle.products.length > 0) {
      // Add bundle as single cart item with bundle pricing
      const bundleCartItem = {
        id: bundle.id,
        title: bundle.name,
        description: bundle.description,
        price: bundle.price,
        original_price: bundle.original_price,
        savings: bundle.savings,
        image_urls: bundle.products.map(p => p.image_urls?.[0]).filter(Boolean),
        isBundle: true,
        bundleProducts: bundle.products,
        productCount: bundle.products.length
      };
      
      addToCart(bundleCartItem);
      trackBundleAddToCart(bundle.id, bundle.name, bundle.price);
      toast.success(`${bundle.name} added to cart!`, {
        description: `Bundle with ${bundle.products.length} items - ₹${bundle.price}`
      });
      onClose();
    } else {
      toast.info('Bundle coming soon!', {
        description: 'Products will be added shortly'
      });
    }
  };

  if (!bundle && !loading) return null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full"></div>
          </div>
        ) : bundle ? (
          <div className="pb-6">
            <SheetHeader className="mb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-cyan-600" />
                  <SheetTitle className="text-lg font-bold pr-8">{bundle.name}</SheetTitle>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="absolute right-4 top-4"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </SheetHeader>

            {/* Bundle Images - Product Collage */}
            <div className="aspect-video rounded-xl overflow-hidden bg-muted mb-4">
              {bundle.products && bundle.products.length > 0 ? (
                <div className="grid grid-cols-2 gap-1 h-full">
                  {bundle.products.slice(0, 4).map((product, idx) => (
                    <div 
                      key={idx} 
                      className={`overflow-hidden ${
                        bundle.products.length === 1 ? 'col-span-2' :
                        bundle.products.length === 3 && idx === 0 ? 'col-span-2' : ''
                      }`}
                    >
                      <img
                        src={product.image_urls?.[0] || bundle.image_url}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <img
                  src={bundle.image_url}
                  alt={bundle.name}
                  className="w-full h-full object-cover"
                />
              )}
            </div>

            {/* Price & Savings */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-cyan-600">₹{bundle.price}</span>
                    <span className="text-lg text-gray-400 line-through">₹{bundle.original_price}</span>
                    <Badge className="bg-green-500">
                      {Math.round((bundle.savings / bundle.original_price) * 100)}% OFF
                    </Badge>
                  </div>
                  <p className="text-sm text-green-600 font-semibold mt-1">
                    You save ₹{bundle.savings}
                  </p>
                </div>
              </div>

              {bundle.age_groups && bundle.age_groups.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {bundle.age_groups.map((age) => (
                    <Badge key={age} variant="outline">
                      {age} years
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <Separator className="my-4" />

            {/* Description */}
            <div className="mb-4">
              <h3 className="font-semibold mb-2">About this Bundle</h3>
              <p className="text-sm text-muted-foreground">
                {bundle.description}
              </p>
            </div>

            {/* Products in Bundle */}
            {bundle.products && bundle.products.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold mb-3">What's Included ({bundle.products.length} items)</h3>
                <div className="space-y-2">
                  {bundle.products.map((product, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white flex-shrink-0">
                        <img
                          src={product.image_urls?.[0] || 'https://via.placeholder.com/48'}
                          alt={product.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.title}</p>
                        <p className="text-xs text-muted-foreground">₹{product.price}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator className="my-4" />

            {/* Add to Cart Button */}
            <Button
              size="lg"
              className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 gap-2"
              onClick={handleAddBundle}
            >
              <ShoppingCart className="w-5 h-5" />
              Add Bundle to Cart - ₹{bundle.price}
            </Button>

            <p className="text-xs text-center text-muted-foreground mt-3">
              <Sparkles className="w-3 h-3 inline mr-1" />
              All items will be added to your cart individually
            </p>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};
